/* ==========================================================================
   LUJE — true availability-aware counts next to each size/color filter
   checkbox, e.g. "M (12)" becoming "M (9)" when 3 of those 12 products'
   M is actually sold out.

   THE GAP THIS FILLS
   Shopify's own filter.values[].count is "how many products have this
   option value on some variant", not "and it's in stock" — same gap
   luje-filter-availability.js fixes for the product grid itself. That file's
   header has the full explanation; this is the counts half of the same fix.
   Shopify computes its count from its own search index, at collection scale;
   there is no client-visible equivalent for "and available", so getting a
   true number means checking every product in the collection ourselves.

   WHY THIS NEEDS ITS OWN BULK FETCH
   luje-filter-availability.js only sees the current page's ~24-48 rendered
   product cards. A count computed from just that page would undercount any
   collection bigger than one page and visibly change as someone paginates —
   worse than Shopify's own (accurate-but-wrong-question) number. So this
   fetches templates/collection.luje-availability.liquid once per collection,
   which is not limited to the current page.

   WHEN IT FETCHES, AND WHEN IT DOESN'T
   - Lazily, the first time the shopper opens the filter drawer — counts are
     only visible once that panel is open, so a visit that never opens it
     costs nothing.
   - Never above ~250 products (data-collection-products-count, rendered by
     storefront-filters.liquid). This store carries a handful of
     multi-hundred/multi-thousand-product legacy collections from the shared
     multi-brand catalog; those just keep exactly today's behaviour — no
     fetch attempted, no cost, no regression. 250 is Shopify's own
     `paginate ... by` ceiling, which is why the endpoint template needs no
     "page 2" handling either.
   - Once per collection per browser tab: cached in sessionStorage. Refetched
     if the cache is missing, unparseable, or older than STALE_MS.

   LIVE, BEFORE APPLY
   luje-filter-drawer.js defers the actual grid update behind an Apply click,
   so the URL is intentionally stale while the shopper is still choosing.
   Counts should not wait for that — a shopper ticking boxes wants to see
   "if I also pick this, N products match" as they go, same as Shopify's own
   filter UI does. So active filters here are read live from the checkboxes'
   :checked state, not from the URL (contrast with luje-filter-availability.js,
   which correctly reads the URL, because grid + URL change together on
   Apply).

   FACETED SEMANTICS
   The count next to an unchecked "M" is "how many products would match if
   this were also checked" — combined with whatever is ALREADY checked in
   OTHER option groups (Color, etc.), but not the candidate's own group
   (sizes are OR'd together within Size, so whether L is also checked must
   not shrink M's own count). Same combined-predicate logic
   luje-filter-availability.js uses to decide whether to hide a card, just
   evaluated per candidate value and counting matching products instead of
   testing one specific product.
   ========================================================================== */
(function () {
  'use strict';

  var FORM = '#CollectionFiltersForm';
  var OPENER = '.sf__sidebar-open';
  var MAX_PRODUCTS = 250;
  var STALE_MS = 10 * 60 * 1000;

  var fetching = null;   // in-flight promise, so a fast double-open doesn't double-fetch
  var dataByProductId = null;

  function form() { return document.querySelector(FORM); }

  function cacheKey(url) { return 'luje:filter-counts:' + url; }

  function readCache(url) {
    try {
      var raw = sessionStorage.getItem(cacheKey(url));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.savedAt !== 'number' || !parsed.products) return null;
      if (Date.now() - parsed.savedAt > STALE_MS) return null;
      return parsed.products;
    } catch (e) { return null; }
  }

  function writeCache(url, products) {
    try {
      sessionStorage.setItem(cacheKey(url), JSON.stringify({ savedAt: Date.now(), products: products }));
    } catch (e) { /* storage full/disabled — just skip caching, still works this load */ }
  }

  /* Builds { productId: [ {optName: value, ...}, ... ] } from the endpoint's
     [{id, variants: [...]}] shape, matching the per-card blob's entry shape
     so the same matching logic reads either. */
  function indexById(products) {
    var byId = {};
    for (var i = 0; i < products.length; i++) {
      byId[products[i].id] = products[i].variants;
    }
    return byId;
  }

  function fetchAvailability(f) {
    var url = f.getAttribute('data-collection-url');
    var count = parseInt(f.getAttribute('data-collection-products-count'), 10) || 0;
    if (!url || count === 0 || count > MAX_PRODUCTS) return Promise.resolve(null);

    var cached = readCache(url);
    if (cached) return Promise.resolve(indexById(cached));

    if (fetching) return fetching;
    fetching = fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=luje-availability')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (text) {
        if (!text) return null;
        var products;
        try { products = JSON.parse(text); } catch (e) { return null; }
        writeCache(url, products);
        return indexById(products);
      })
      .catch(function () { return null; })
      .then(function (result) { fetching = null; return result; });
    return fetching;
  }

  /* Every checked box, grouped by option name — e.g. {size: ["M"], color: ["Blue","Black"]}. */
  function checkedByGroup(f) {
    var groups = {};
    var boxes = f.querySelectorAll('input[type="checkbox"][name^="filter.v.option."]:checked');
    for (var i = 0; i < boxes.length; i++) {
      var name = boxes[i].name.replace('filter.v.option.', '');
      (groups[name] || (groups[name] = [])).push(boxes[i].value);
    }
    return groups;
  }

  function countMatching(byId, candidateName, candidateValue, otherGroups) {
    var otherNames = Object.keys(otherGroups);
    var total = 0;
    for (var id in byId) {
      var variants = byId[id];
      for (var i = 0; i < variants.length; i++) {
        var entry = variants[i];
        if (entry[candidateName] !== candidateValue) continue;
        var ok = true;
        for (var j = 0; j < otherNames.length; j++) {
          var n = otherNames[j];
          if (otherGroups[n].indexOf(entry[n]) === -1) { ok = false; break; }
        }
        if (ok) { total++; break; } /* one matching variant is enough; move to next product */
      }
    }
    return total;
  }

  function render(f) {
    if (!dataByProductId) return;
    var checked = checkedByGroup(f);
    var boxes = f.querySelectorAll('input[type="checkbox"][name^="filter.v.option."]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var name = box.name.replace('filter.v.option.', '');
      var otherGroups = {};
      for (var g in checked) { if (g !== name) otherGroups[g] = checked[g]; }

      var label = box.closest('label');
      var span = label && label.querySelector('.filter-products-count');
      if (!span) continue;

      if (!span.dataset.lujeOriginal) span.dataset.lujeOriginal = span.textContent;
      var n = countMatching(dataByProductId, name, box.value, otherGroups);
      span.textContent = '(' + n + ')';
    }
  }

  function primeAndRender() {
    var f = form();
    if (!f) return;
    if (dataByProductId) { render(f); return; }
    fetchAvailability(f).then(function (byId) {
      if (!byId) return; /* too large, no data, or fetch failed — leave Shopify's own counts as-is */
      dataByProductId = byId;
      var fresh = form();
      if (fresh) render(fresh);
    });
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest(OPENER)) primeAndRender();
  }, true);

  /* Live updates as the shopper ticks boxes, ahead of Apply. Capture phase on
     document runs before luje-filter-drawer.js's capture listener on the
     form (which stopImmediatePropagation()s real checkbox events) — document
     is reached first in the capture pass, so this always sees the event
     regardless of what that listener does afterwards. */
  document.addEventListener('change', function (e) {
    var f = form();
    if (f && dataByProductId && f.contains(e.target)) render(f);
  }, true);

  /* The AJAX re-render swaps the whole section, including the sidebar/form,
     so re-render against whatever form exists now. Data itself doesn't need
     re-fetching — it isn't scoped to the current filter selection. */
  function watch() {
    new MutationObserver(function (muts) {
      var swapped = muts.some(function (m) {
        return [].slice.call(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (n.matches && n.matches(FORM) || n.querySelector && n.querySelector(FORM));
        });
      });
      if (swapped && dataByProductId) {
        var f = form();
        if (f) render(f);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
